import { ThemeProvider } from '@material-ui/core/styles'
import ActivityStage from 'components/activity-plans/stage'
import AppSnackbarProvider from 'components/app-snackbar-provider'
import ABTestPage from 'pages/ab'
import AcceptInvitation from 'pages/accept-invitation'
import AssumeUserPage from 'pages/assume-user'
import BookingConfirmationPage from 'pages/booking-confirmation'
import LogoutPage from 'pages/logout'
import LoggedOutPage from 'pages/logged-out'
import SinglePlansPublicPage from 'pages/pga-coach/activity-plans/stage/activity-plans/SingleActivityPlanPublic'
import CreateEvent from 'pages/pga-coach/bookings/events/new'
import CreateOnboardingEvent from 'pages/pga-coach/bookings/events/new/onboarding'
import CreateProfile from 'pages/pga-coach/profile/new'
import CreateProfileOnboarding from 'pages/pga-coach/profile/new/onboarding'
import PublicActivityPlansPage from 'pages/public-activity-plans'
import ScheduleSessionFlow from 'pages/schedule'
import CoachLeads from 'pages/coach-leads'
import WelcomePage from 'pages/welcome'
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
} from 'react-router-dom'
import AchievementsRoutes from 'routes/achievements-routes'
import JuniorLeagueMarketingRoutes from 'routes/junior-league-marketing-routes'
import JuniorLeagueRegistrationRoutes from 'routes/junior-league-registration-routes'
import JuniorLeagueRoutes from 'routes/junior-league-routes'
import JuniorLeagueSignupRoutes from 'routes/junior-league-signup-routes'
import ThingsToDoRoutes from 'routes/things-to-do-routes'
import themes, { extendTheme } from '../themes'
import AcademyRoutes from './academy-routes'
import AccountRoutes from './account-routes'
import AdminRoutes from './admin-routes'
import ClubRoutes from './club-routes'
import JuniorLeagueManagerRoutes from './junior-league-manager-routes'
import PgaCoachRoutes from './pga-coach-routes'
import Ideas from '../pages/ideas'
import WaiverPage from '../pages/waiver'
import ExternalRedirect from 'components/external-redirect'
import { MY_PGA_COM_HOST } from 'env'
import PaymentRequestPage from '../pages/payment-request'
import AttendeeRsvpPage from 'pages/attendee-rsvp'
import CheckoutSuccessPage from 'pages/checkout/success'
import PgaCoachCheckoutSuccessPage from 'pages/pga-coach/checkout-success'
import AcceptSponsorshipPage from 'pages/pga-coach/accept-sponsorship'
import PurchaseProductConfirmation from 'pages/product-purchase-confirmation'
import MerchCarouselPreview from 'pages/junior-league-player/dev/merch-preview'
import { useIsMobile } from 'lib/hooks'
import { useDefaultLauncherVisibility } from 'lib/support'
import { shouldShowStandaloneIntercomLauncher } from './intercom-launcher-visibility'

const StandaloneIntercomLauncherPolicy = () => {
  const { pathname } = useLocation()
  const isMobile = useIsMobile()

  useDefaultLauncherVisibility(
    !isMobile && shouldShowStandaloneIntercomLauncher(pathname),
  )

  return null
}

function Routes() {
  const bookingEventsNewUrl = `${MY_PGA_COM_HOST}/pga-coach/bookings/events/new`
  return (
    <Router>
      <StandaloneIntercomLauncherPolicy />
      <Switch>
        <Route path="/coaching-center">
          <AcademyRoutes />
        </Route>
        <Route path="/pga-coaching-center-at-pga-frisco">
          <AcademyRoutes />
        </Route>
        <Route path={'/family-golf'}>
          <ExternalRedirect
            externalUrl={bookingEventsNewUrl}
            destination="PGA Coach Group Event Creation"
          />
        </Route>
        <Route path={'/family-golf-captain'}>
          <ExternalRedirect
            externalUrl={bookingEventsNewUrl}
            destination="PGA Coach Group Event Creation"
          />
        </Route>
        <Route
          exact
          path="/pga-coach/bookings/events/new"
          component={CreateEvent}
        />
        <Route
          exact
          path="/pga-coach/checkout/success"
          component={PgaCoachCheckoutSuccessPage}
        />
        <Route
          exact
          path="/pga-coach/onboarding/bookings/events/new"
          component={() => <CreateOnboardingEvent />}
        />
        <Route exact path="/pga-coach/profile/new" component={CreateProfile} />
        <Route
          exact
          path="/pga-coach/onboarding/profile/new"
          component={CreateProfileOnboarding}
        />
        <Route exact path="/pga-coach/app">
          <ExternalRedirect
            externalUrl="https://onelink.to/pga-coach"
            destination="download PGA Coach App"
          />
        </Route>
        <Route exact path="/pga-coach/coach-endorsements/:id/accept">
          <ThemeProvider theme={extendTheme(themes.pgaCoach)}>
            <AppSnackbarProvider
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <AcceptSponsorshipPage />
            </AppSnackbarProvider>
          </ThemeProvider>
        </Route>
        <Route path="/pga-coach">
          <PgaCoachRoutes />
        </Route>
        <Route exact path="/club/app">
          <ExternalRedirect
            externalUrl="https://onelink.to/mypga"
            destination="download MyPGA App"
          />
        </Route>
        <Route path="/club">
          <ClubRoutes />
        </Route>
        <Route path="/invitation/:connectionId" component={AcceptInvitation} />
        <Route
          path="/payment/:paymentRequestToken"
          component={PaymentRequestPage}
        />
        <Route exact path="/coach/:idOrSlug/schedule">
          <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
            <ScheduleSessionFlow />
          </ThemeProvider>
        </Route>
        <Route exact path="/coach/:idOrSlug/contact">
          <CoachLeads />
        </Route>
        <Route exact path="/coach/:idOrSlug/purchase/lesson-pack">
          <AppSnackbarProvider
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <PurchaseProductConfirmation />
          </AppSnackbarProvider>
        </Route>
        <Route
          exact
          path={[
            '/coach/:idOrSlug/student/:studentId/purchase/private-lesson/:lessonTypeId',
            '/coach/:idOrSlug/purchase/private-lesson/:lessonTypeId',
          ]}
        >
          <AppSnackbarProvider
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <PurchaseProductConfirmation />
          </AppSnackbarProvider>
        </Route>
        <Route path={'/family-golf-signup'}>
          <ExternalRedirect
            externalUrl={bookingEventsNewUrl}
            destination="PGA Coach Group Event Creation"
          />
        </Route>
        {/* TODO: new path for Family Golf */}
        <Route path="/events">
          <ExternalRedirect
            externalUrl={bookingEventsNewUrl}
            destination="PGA Coach Group Event Creation"
          />
        </Route>
        <Route path={'/family-golf-registration'}>
          <ExternalRedirect
            externalUrl={bookingEventsNewUrl}
            destination="PGA Coach Group Event Creation"
          />
        </Route>
        <Route path={'/family-golf-invitation'}>
          <ExternalRedirect
            externalUrl={bookingEventsNewUrl}
            destination="PGA Coach Group Event Creation"
          />
        </Route>
        <Route path="/junior-league">
          <JuniorLeagueRoutes />
        </Route>
        <Route path="/junior-league-events">
          <JuniorLeagueMarketingRoutes />
        </Route>
        <Route path="/junior-league-signup">
          <JuniorLeagueSignupRoutes />
        </Route>
        <Route exact path="/junior-league-registration/dev/merch-preview">
          <ThemeProvider theme={extendTheme(themes.juniorLeague)}>
            <AppSnackbarProvider
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <MerchCarouselPreview />
            </AppSnackbarProvider>
          </ThemeProvider>
        </Route>
        <Route exact path="/junior-league-registration/dev/merch-preview/:flow">
          <ThemeProvider theme={extendTheme(themes.juniorLeague)}>
            <AppSnackbarProvider
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <MerchCarouselPreview />
            </AppSnackbarProvider>
          </ThemeProvider>
        </Route>
        <Route path="/junior-league-registration">
          <JuniorLeagueRegistrationRoutes />
        </Route>
        <Route path="/junior-league-manager">
          <JuniorLeagueManagerRoutes />
        </Route>
        <Route path="/things-to-do">
          <ThingsToDoRoutes />
        </Route>
        <Route path="/achievements">
          <AchievementsRoutes />
        </Route>
        <Route exact path="/booking-confirmation/:confirmationCode">
          <ThemeProvider theme={extendTheme(themes.pgaCoach)}>
            <AppSnackbarProvider
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <BookingConfirmationPage />
            </AppSnackbarProvider>
          </ThemeProvider>
        </Route>
        <Route path="/admin" component={AdminRoutes} />
        <Route path="/account" component={AccountRoutes} />
        <Route exact path="/" component={WelcomePage} />
        <Route exact path="/welcome">
          <Redirect to="/" />
        </Route>
        <Route exact path="/ab" component={ABTestPage} />
        <Route exact path="/assume-user" component={AssumeUserPage} />
        <Route exact path="/checkout/success" component={CheckoutSuccessPage} />
        <Route exact path="/ideas" component={Ideas} />
        <Route exact path="/logout" component={LogoutPage} />
        <Route exact path="/logged-out" component={LoggedOutPage} />
        <Route path="/waivers/:waiverId?">
          <AppSnackbarProvider
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <WaiverPage />
          </AppSnackbarProvider>
        </Route>
        <Route
          exact
          path="/activity-plan/:id"
          component={SinglePlansPublicPage}
        />
        <Route
          exact
          path="/activity-plans"
          component={PublicActivityPlansPage}
        />
        <Route path="/activity-plans/:stage" component={ActivityStage} />
        <Route
          path="/dues"
          exact
          component={() => {
            window.location.replace('/member/dues')
            return null
          }}
        />
        <Route
          exact
          path="/event/:eventId/lesson/:lessonId/attendance/:contactId"
        >
          <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
            <AppSnackbarProvider
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <AttendeeRsvpPage />
            </AppSnackbarProvider>
          </ThemeProvider>
        </Route>
        <Route path="*" render={() => <div>Page Not Found</div>} />
      </Switch>
    </Router>
  )
}

export default Routes
